import React, { memo, useCallback, useState, useContext, useMemo } from 'react';
import { SheetBottom } from '@nexus/lib-react/dist/core/SheetBottom/SheetBottom';
import { Modal } from '@nexus/lib-react/dist/core/Modal/Modal';
import { LinkParagraph } from '@nexus/lib-react/dist/core/Link/LinkParagraph';
import { Headline3, Paragraph2 } from '@nexus/lib-react/dist/core/Typography';
import { ButtonTertiaryDark } from '@nexus/lib-react/dist/core/Button/ButtonTertiaryDark';
import { ButtonPrimaryDark } from '@nexus/lib-react/dist/core/Button/ButtonPrimaryDark';
import { useTrackEvent } from '@olxeu-a2b/delivery-utils/src/tracking/useNinja';
import trackingKeys from '../../../../commons/constants/trackingKeys';
import TransactionContext from '../contexts/TransactionContext';
import useI18n from '../../../../i18n/hooks/useI18n';
import { EXTERNAL_LINKS, SERVICE_FEE_PRICE } from '../../constants';
import type { PriceComponent } from '../../../../../typings/transaction';

import S from './style/InvoiceBtnLink';

interface ModalProps {
  handleCloseModal: () => void;
  handleCloseModalOnAction: () => void;
  isDesktop: boolean;
  isVisible: boolean;
  isBTRRebrandingFlagOn: boolean;
}

interface BtnLinkProps {
  desktop: boolean;
  isBTRRebrandingFlagOn: boolean;
}

const InvoiceModal: React.FC<ModalProps> = function InvoiceModal({
  handleCloseModal,
  handleCloseModalOnAction,
  isDesktop,
  isVisible,
  isBTRRebrandingFlagOn,
}: ModalProps) {
  const i18n = useI18n();
  const headerContent = isBTRRebrandingFlagOn
    ? i18n`dlv_service_fee_request_invoice_header_BTR_rebrand`
    : i18n`dlv_service_fee_request_invoice_header`;

  const actionButton = (
    <ButtonPrimaryDark
      data-cy="customer-support-btn"
      data-testid="customer-support-btn"
      as="a"
      href={EXTERNAL_LINKS.CREATE_REQUEST}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleCloseModalOnAction}
    >
      {i18n`dlv_service_fee_customer_support`}
    </ButtonPrimaryDark>
  );

  const modalContent = (
    <>
      <Paragraph2 pb={16} textAlign="center">
        {isBTRRebrandingFlagOn
          ? i18n`dlv_service_fee_request_invoice_text_pt_1_BTR_rebrand`
          : i18n`dlv_service_fee_request_invoice_text_pt_1`}
      </Paragraph2>
      <Paragraph2 pb={16} textAlign="center">
        {i18n`dlv_service_fee_request_invoice_text_pt_2`}
      </Paragraph2>
    </>
  );

  return isDesktop ? (
    isVisible && (
      <S.ModalWrapper
        position="center"
        data-cy="invoice-modal-desktop"
        data-testid="invoice-modal-desktop"
        showCloseIcon
        onClose={handleCloseModal}
        maskClosable
        closableOnEscPress
        footer={
          <Modal.Footer>
            <ButtonTertiaryDark mr={32} onClick={handleCloseModal}>
              {i18n`dlv_generics_no`}
            </ButtonTertiaryDark>
            {actionButton}
          </Modal.Footer>
        }
      >
        <Headline3 pb={24} textAlign="center">
          {headerContent}
        </Headline3>
        {modalContent}
      </S.ModalWrapper>
    )
  ) : (
    <SheetBottom
      closeButtonText={i18n`dlv_generics_no`}
      isVisible={isVisible}
      onClose={handleCloseModal}
      data-cy="invoice-modal-mobile"
      data-testid="invoice-modal-mobile"
    >
      <Headline3 pb={16} textAlign="center">
        {headerContent}
      </Headline3>
      {modalContent}
      {actionButton}
    </SheetBottom>
  );
};

const InvoiceLink: React.FC<BtnLinkProps> = function InvoiceLink({
  desktop,
  isBTRRebrandingFlagOn,
}) {
  const { order, ad } = useContext(TransactionContext) || {};
  const [isModalOpened, setModalOpened] = useState(false);
  const i18n = useI18n();

  const {
    events: {
      serviceFeeInvoiceLinkClick,
      serviceFeeCustomerServiceCancelClick,
      serviceFeeCustomerServiceClick,
    },
  } = trackingKeys;
  const getServiceFeePrice = (priceComponents?: PriceComponent[]) => {
    const serviceFee = priceComponents.find(component => component.code === SERVICE_FEE_PRICE);
    return serviceFee ? serviceFee.value.amount : 0;
  };
  const serviceFeePrice = getServiceFeePrice(order.price.components);

  const trackExtraData = useMemo(
    () => ({
      ad_id: ad?.id,
      ad_price: order.price.total.amount,
      touch_point_page: 'delivery_overview',
      touch_point_button: 'request_invoice_link',
      service_fee: serviceFeePrice,
    }),
    [order, ad, serviceFeePrice],
  );
  const trackEvent = useTrackEvent();
  const trackEventWithExtraData = useCallback(
    event => trackEvent(event, null, null, trackExtraData),
    [trackEvent, trackExtraData],
  );

  const handleOnClick = useCallback(
    e => {
      e.preventDefault();
      setModalOpened(true);
      trackEventWithExtraData(serviceFeeInvoiceLinkClick);
    },
    [setModalOpened, trackEventWithExtraData],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpened(false);
    trackEventWithExtraData(serviceFeeCustomerServiceCancelClick);
  }, [setModalOpened, trackEventWithExtraData]);

  const handleCloseModalOnAction = useCallback(() => {
    setModalOpened(false);
    trackEventWithExtraData(serviceFeeCustomerServiceClick);
  }, [setModalOpened, trackEventWithExtraData]);

  return (
    <>
      <S.ButtonLink>
        <LinkParagraph onClick={handleOnClick}>
          {isBTRRebrandingFlagOn
            ? i18n`dlv_service_fee_request_invoice_link_rebrand`
            : i18n`dlv_service_fee_request_invoice_link`}
        </LinkParagraph>
      </S.ButtonLink>
      <InvoiceModal
        handleCloseModal={handleCloseModal}
        handleCloseModalOnAction={handleCloseModalOnAction}
        isDesktop={desktop}
        isVisible={isModalOpened}
        isBTRRebrandingFlagOn={isBTRRebrandingFlagOn}
      />
    </>
  );
};

export default memo(InvoiceLink);
